const BASE_GAIN_MUSIC = 0.3;
const BASE_GAIN_MASTER = 0.5;

export class AudioSystem {
  readonly ctx = new AudioContext();
  masterGain: GainNode;
  musicBus: GainNode;
  soundBus: GainNode;
  locked = true;

  constructor() {
    this.masterGain = this.ctx.createGain();
    this.masterGain.connect(this.ctx.destination);
    this.masterGain.gain.value = BASE_GAIN_MASTER;
    this.musicBus = this.ctx.createGain();
    this.musicBus.connect(this.masterGain);
    this.musicBus.gain.value = BASE_GAIN_MUSIC;
    this.soundBus = this.ctx.createGain();
    this.soundBus.connect(this.masterGain);
  }

  unlock(): void {
    if (this.locked && this.ctx.state === "suspended") {
      this.ctx.resume();
    }
    this.locked = false;
  }

  setMasterVolume(v: number): void {
    this.masterGain.gain.value = v * BASE_GAIN_MASTER;
  }

  setMusicVolume(v: number): void {
    this.musicBus.gain.value = v * BASE_GAIN_MUSIC;
  }

  playSound(buffer: AudioBuffer, volume = 1): void {
    if (this.locked) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    const gain = this.ctx.createGain();
    gain.gain.value = volume;
    src.connect(gain).connect(this.soundBus);
    src.start();
  }

  playSoundAt(buffer: AudioBuffer, x: number, screenW: number, volume = 1, maxDist = 200): GainNode | null {
    if (this.locked) return null;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    const gain = this.ctx.createGain();
    const panner = this.ctx.createStereoPanner();
    const halfW = screenW * 0.5;
    const dx = x - halfW;
    panner.pan.value = dx / halfW;
    const dist = Math.abs(dx);
    const attenuation = dist > maxDist ? 0 : 1 - dist / maxDist;
    gain.gain.value = volume * attenuation;
    src.connect(gain).connect(panner).connect(this.soundBus);
    src.start();
    return gain;
  }

  playMusic(buffer: AudioBuffer, loop = true, loopStart?: number, loopEnd?: number): AudioBufferSourceNode {
    if (this.ctx.state === "suspended") this.ctx.resume();
    this.locked = false;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = loop;
    if (loop && loopStart !== undefined) src.loopStart = loopStart;
    if (loop && loopEnd !== undefined) src.loopEnd = loopEnd;
    src.connect(this.musicBus);
    src.start();
    return src;
  }

  createLoopingSource(buffer: AudioBuffer): { src: AudioBufferSourceNode; gain: GainNode } {
    if (this.ctx.state === "suspended") this.ctx.resume();
    this.locked = false;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    const gain = this.ctx.createGain();
    gain.gain.value = 0;
    src.connect(gain).connect(this.soundBus);
    src.start();
    return { src, gain };
  }

  fadeMusicIn(src: AudioBufferSourceNode, duration: number): void {
    const now = this.ctx.currentTime;
    this.musicBus.gain.setValueAtTime(0, now);
    this.musicBus.gain.linearRampToValueAtTime(BASE_GAIN_MUSIC, now + duration);
  }

  fadeMusicOut(duration: number): AudioBufferSourceNode | null {
    const now = this.ctx.currentTime;
    this.musicBus.gain.setValueAtTime(this.musicBus.gain.value, now);
    this.musicBus.gain.linearRampToValueAtTime(0, now + duration);
    return null;
  }
}
